import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeftCircle,
  Send,
  Database,
  AlertTriangle
} from 'lucide-react';
import { MessageData } from '../../hooks/use-messages';
import { useAuth } from '../../hooks/use-auth';

interface MessageThreadProps {
  message: MessageData;
  onBack: () => void;
  onSend: (content: string, recipientId: string, subject?: string) => void;
  onAttachData?: () => void;
}

/**
 * MessageError component to display when conversation has invalid data
 */
const MessageError: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className="flex flex-col items-center justify-center p-8 h-full">
    <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-lg text-center max-w-md">
      <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-600 dark:text-red-400" />
      <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">Something went wrong</h3>
      <p className="text-red-700 dark:text-red-300 mb-4">
        We encountered an issue loading this conversation. This might be due to missing or corrupted data.
      </p>
      <button
        onClick={onBack}
        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
      >
        Try again
      </button>
    </div>
  </div>
);

/**
 * MessageThread component displays the details of a single message 
 * (conceptually, the head of a thread) and allows replying.
 */
const MessageThread: React.FC<MessageThreadProps> = ({
  message,
  onBack,
  onSend,
  onAttachData
}) => {
  const [newMessage, setNewMessage] = useState('');
  const { user } = useAuth();
  
  // Validate message data
  if (!message) { 
    return <MessageError onBack={onBack} />;
  }
  
  // Since this component now shows a single message detail,
  // the concept of listing multiple messages within it is removed.
  // We display the content of the passed 'message' prop.
  
  // Scroll to bottom effect might not be needed if only showing one message + reply box
  // useEffect(() => {
  //   messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  // }, [message]); // Trigger when the message changes
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newMessage.trim() && message?.sender_id && user?.id) {
      // Determine recipient based on who sent the original message
      const recipientId = message.sender_id; // Reply to the sender
      // Pass content, recipientId, and potentially the original subject for context
      onSend(newMessage.trim(), recipientId, message.subject || undefined);
      setNewMessage('');
    }
  };
  
  // Check if the message has valid data (basic check)
  const hasValidData = message && message.id;
  if (!hasValidData) {
    return <MessageError onBack={onBack} />;
  }

  // Determine if the message was sent by the current user or the provider/sender
  const isCurrentUserSender = message.sender_id === user?.id;
  const partnerName = isCurrentUserSender ? message.recipient_name : message.sender_name;
  const partnerRole = isCurrentUserSender ? message.recipient_role : message.sender_role;
  
  // Format timestamp safely
  let formattedTimestamp = 'Invalid Date';
  let formattedFullDate = 'Invalid Date';
  try {
    const parsedDate = parseISO(message.timestamp);
    formattedTimestamp = format(parsedDate, 'h:mm a');
    formattedFullDate = format(parsedDate, 'MMMM d, yyyy');
  } catch (e) {
    console.error("Error parsing date:", message.timestamp, e);
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Mobile back button */}
      <div className="md:hidden flex-shrink-0 p-2 bg-[var(--background-subtle)]">
        <button 
          onClick={onBack}
          className="flex items-center px-3 py-2 rounded-md text-[var(--text-primary)] hover:bg-[var(--background-interactive)]"
          aria-label="Back to messages"
        >
          <ArrowLeftCircle size={18} className="mr-2" />
          <span className="font-medium">Back to messages</span>
        </button>
      </div>
      
      {/* Header - Display partner info */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
        <div className="flex items-center">
          {/* Placeholder Avatar */}
          <div className="w-10 h-10 rounded-full bg-[var(--background-subtle)] flex items-center justify-center mr-3">
            <span className="text-[var(--text-secondary)] font-semibold">
              {partnerName?.charAt(0).toUpperCase() ?? 'P'}
            </span>
          </div>
          <div>
            <h2 className="font-medium text-[var(--text-primary)]">
              {partnerName ?? 'Unknown Sender'}
            </h2>
            <p className="text-xs text-[var(--text-secondary)]">
              {partnerRole ?? 'Unknown Role'}
            </p>
          </div>
        </div>
        {/* Remove urgent indicator if not present in MessageData */}
        {/* <div> ... </div> */}
      </div>
      
      {/* Subject line */}
      <div className="flex-shrink-0 p-4 bg-[var(--background-subtle)] border-b border-[var(--border-subtle)]">
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          Subject: {message.subject ?? 'No Subject'}
        </h3>
      </div>
      
      {/* Message Content - Display the single message passed */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {/* Date Separator */}
          <div className="flex justify-center">
            <span className="text-xs bg-[var(--background-subtle)] px-2 py-1 rounded-full text-[var(--text-secondary)]">
              {formattedFullDate}
            </span>
          </div>
          
          {/* The Message Bubble */}
          <div className={`flex ${!isCurrentUserSender ? 'justify-start' : 'justify-end'}`}>
            <div 
              className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${
                !isCurrentUserSender 
                  ? 'bg-white/60 dark:bg-slate-700/50 backdrop-blur-md border border-[var(--border-subtle)] text-[var(--text-primary)]' 
                  : 'bg-[var(--brand-primary)] text-white'
              }`}
            >
              <p>{message.content}</p>
              <div className={`text-xs mt-1 ${!isCurrentUserSender ? 'text-[var(--text-secondary)]' : 'text-white/70'}`}>
                {formattedTimestamp}
              </div>
            </div>
          </div>
        </div>
        {/* Remove messagesEndRef if scrollIntoView is not used */}
        {/* <div ref={messagesEndRef} /> */}
      </div>
      
      {/* Compose Reply */}
      <div className="flex-shrink-0 p-4 border-t border-[var(--border-subtle)]">
        <form onSubmit={handleSendMessage} className="flex items-end gap-2">
          {/* Text Input */}
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={`Reply to ${partnerName}...`}
            className="flex-1 p-3 border rounded-lg bg-[var(--background-surface)] text-[var(--text-primary)] resize-none min-h-[40px] max-h-[150px]"
            rows={1} // Start with 1 row, auto-expand
            onInput={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              // Auto-resize logic
              e.target.style.height = 'auto';
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
          />
          {onAttachData && (
            <button
              type="button"
              onClick={onAttachData}
              className="p-2 rounded-full text-[var(--text-secondary)] hover:bg-[var(--background-subtle)] hover:text-[var(--brand-primary)]"
              title="Attach health data"
            >
              <Database size={18} />
            </button>
          )}
          
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className={`
              p-3 rounded-lg flex items-center justify-center
              ${newMessage.trim() 
                ? 'bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white' 
                : 'bg-[var(--background-subtle)] text-[var(--text-disabled)]'
              }
              transition-colors
            `}
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default MessageThread; 